import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material'
import { useCallback, useEffect, useState } from 'react'

import { api, ApiError, type GpuStatus } from '../api/client'
import { estimateBatch, formatRange } from '../lib/estimate'
import type { BatchItem } from '../order/useBatch'

/** How long to keep waiting for the API after a power-on before giving up.
 *  Measured on the real instance: Scaleway says running about ten seconds in,
 *  the API answers roughly twenty seconds after that. */
const READY_TIMEOUT_MS = 240_000
const READY_POLL_MS = 5_000

interface Props {
  open: boolean
  files: BatchItem[]
  targetCount: number
  modelCount: number
  separates: boolean
  /** tone_change above zero, which forces a separation pass of its own. */
  tonal: boolean
  onCancel: () => void
  onConfirm: () => Promise<void> | void
}

/**
 * The last stop before a real bill.
 *
 * With no quotas in place this dialog is the only thing between a mis-click and
 * half an hour of GPU, so it says plainly how much work is about to be queued
 * rather than asking a generic "are you sure?".
 */
export default function ConfirmOrderDialog({
  open,
  files,
  targetCount,
  modelCount,
  separates,
  tonal,
  onCancel,
  onConfirm,
}: Props) {
  const [status, setStatus] = useState<GpuStatus | null>(null)
  const [checking, setChecking] = useState(true)
  const [statusFailed, setStatusFailed] = useState(false)
  const [busy, setBusy] = useState(false)
  const [waiting, setWaiting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    // Nothing may be ordered until this answers. With the GPU off the check
    // takes seconds -- a connect that never completes -- and an enabled Order
    // button in the meantime sends the order down the plain path, where it
    // fails instead of offering to power the GPU on.
    setError(null)
    setChecking(true)
    setStatusFailed(false)
    let cancelled = false
    api
      .gpuStatus()
      .then((next) => {
        if (!cancelled) setStatus(next)
      })
      .catch(() => {
        if (!cancelled) setStatusFailed(true)
      })
      .finally(() => {
        if (!cancelled) setChecking(false)
      })
    return () => {
      cancelled = true
    }
  }, [open])

  const estimate = estimateBatch({
    durations: files.map((item) => item.durationSeconds ?? null),
    targetCount,
    modelCount,
    separates,
    tonal,
  })

  const waitUntilReady = useCallback(async () => {
    const deadline = Date.now() + READY_TIMEOUT_MS
    for (;;) {
      await new Promise((resolve) => setTimeout(resolve, READY_POLL_MS))
      const next = await api.gpuStatus()
      setStatus(next)
      if (next.state === 'online') return true
      if (Date.now() > deadline) return false
    }
  }, [])

  const confirm = useCallback(async () => {
    setBusy(true)
    setError(null)
    try {
      if (status && status.state !== 'online') {
        // Powering on is what starts the meter, so it happens here, on an
        // explicit click, rather than quietly as a side effect of ordering.
        if (status.available_actions.includes('poweron')) {
          setStatus(await api.gpuPowerOn())
        }
        setWaiting(true)
        const ready = await waitUntilReady()
        setWaiting(false)
        if (!ready) {
          setError(
            'The GPU did not come online in time. It may be out of capacity — nothing was ordered.',
          )
          setBusy(false)
          return
        }
      }
      await onConfirm()
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Could not start the order.')
      setWaiting(false)
    } finally {
      setBusy(false)
    }
  }, [status, waitUntilReady, onConfirm])

  const offline = status !== null && status.state !== 'online'
  const canPowerOn = status?.available_actions.includes('poweron') ?? false

  return (
    <Dialog open={open} onClose={busy ? undefined : onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>
        Order {files.length} cover{files.length === 1 ? '' : 's'}?
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <DialogContentText component="div">
            <Typography component="p" gutterBottom>
              {files.length} file{files.length === 1 ? '' : 's'} · {estimate.coversPerFile} cover
              {estimate.coversPerFile === 1 ? '' : 's'} each ·{' '}
              <strong>{estimate.totalCovers} in total</strong>
            </Typography>
            <Typography component="p">
              Roughly <strong>{formatRange(estimate)}</strong> of GPU time, billed by the hour. This
              is an estimate, not a quote.
            </Typography>
          </DialogContentText>

          {estimate.unknownDurations > 0 && (
            <Alert severity="info">
              {estimate.unknownDurations} file
              {estimate.unknownDurations === 1 ? ' has' : 's have'} a length the browser could not
              read, so the estimate for {estimate.unknownDurations === 1 ? 'it' : 'them'} is a
              guess.
            </Alert>
          )}

          {checking && (
            <Stack direction="row" spacing={1.5} alignItems="center">
              <CircularProgress size={18} />
              <Typography variant="body2">Checking whether the GPU is awake…</Typography>
            </Stack>
          )}

          {statusFailed && (
            <Alert severity="error">
              The GPU&apos;s state could not be read, so this cannot be ordered safely right now.
            </Alert>
          )}

          {!checking && offline && (
            <Alert severity="warning">
              {canPowerOn
                ? 'The GPU is off. Confirming will power it on first, which starts billing, then order once it is ready.'
                : status?.detail}
            </Alert>
          )}

          {waiting && (
            <Stack direction="row" spacing={1.5} alignItems="center">
              <CircularProgress size={18} />
              <Typography variant="body2">
                Waiting for the GPU to come online. This takes a minute or two.
              </Typography>
            </Stack>
          )}

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => void confirm()}
          disabled={
            busy || checking || statusFailed || files.length === 0 || (offline && !canPowerOn)
          }
        >
          {checking ? 'Checking…' : offline && canPowerOn ? 'Power On and Order' : 'Order'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
